/**
 * EditPageSection - Collapsible Section for Edit Pages
 *
 * A reusable section component for organizing form fields within edit pages.
 * Features:
 * - Collapsible header with icon
 * - Consistent styling
 * - Optional badge for counts
 */

import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface IEditPageSectionProps {
	title: string;
	icon?: React.ReactNode;
	children: React.ReactNode;
	isCollapsible?: boolean;
	defaultExpanded?: boolean;
	badge?: string | number;
	badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'success';
	className?: string;
	headerClassName?: string;
	contentClassName?: string;
}

export const EditPageSection = ({
	title,
	icon,
	children,
	isCollapsible = true,
	defaultExpanded = true,
	badge,
	badgeVariant = 'secondary',
	className,
	headerClassName,
	contentClassName,
}: IEditPageSectionProps) => {
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);

	return (
		<div className={cn('rounded-2xl border border-border/70 bg-card/85 shadow-sm', className)}>
			{/* Header */}
			{title && (
				<button
					type='button'
					onClick={() => isCollapsible && setIsExpanded(!isExpanded)}
					className={cn(
						'w-full flex items-center justify-between px-4 py-3 text-left sm:px-5',
						'transition-colors hover:bg-muted/40',
						isCollapsible && 'cursor-pointer',
						headerClassName
					)}
				>
					<div className='flex items-center gap-2'>
						{icon && <span className='text-muted-foreground'>{icon}</span>}
						<h3 className='text-sm font-semibold'>{title}</h3>
						{badge !== undefined && (
							<span
								className={cn(
									'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
									badgeVariant === 'success' && 'bg-green-100 text-green-800',
									badgeVariant === 'destructive' && 'bg-red-100 text-red-800',
									badgeVariant === 'default' && 'bg-primary/10 text-primary',
									badgeVariant === 'secondary' && 'bg-muted text-muted-foreground'
								)}
							>
								{badge}
							</span>
						)}
					</div>
					{isCollapsible && (
						<span className='text-muted-foreground'>
							{isExpanded ? (
								<ChevronUp className='h-4 w-4' />
							) : (
								<ChevronDown className='h-4 w-4' />
							)}
						</span>
					)}
				</button>
			)}

			{/* Content */}
			{isExpanded && (
				<div className={cn('border-t border-border/60 px-4 pb-4 pt-4 sm:px-5', contentClassName)}>
					{children}
				</div>
			)}
		</div>
	);
};

export default EditPageSection;